// BTReader.tsx
import React, { useMemo } from 'react';
import BTBox from '../BTBox';
import theme from '../../theme';
import BTButton from '../BTButton';
import BTInputBox from '../BTInputBox';
import BTMultilineHTMLReader from './BTMultilineHTMLReader';

interface BTReaderProps {
  title: string;
  contents: string;
  createdBy?: string;
  createdAt?: string;
  attachedFiles?: string[];
  onDownloadAll?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onBack?: () => void;
}

const BTReader: React.FC<BTReaderProps> = ({
  title,
  contents,
  attachedFiles = [],
  onDownloadAll,
  onEdit,
  onDelete,
  onBack,
}) => {
  const processedHtml = useMemo(() => {
    // 이미지에 반응형 스타일을 추가하되 기존 스타일을 보존
    const result = contents.replace(/<img([^>]*?)>/gi, (match, attributes) => {
      // style 속성이 있는지 확인
      const styleMatch = attributes.match(/style="([^"]*?)"/);
      
      if (styleMatch) {
        // 기존 스타일이 있으면 반응형 스타일 추가
        const existingStyle = styleMatch[1];
        const newStyle = `${existingStyle}; max-width: 100%; height: auto;`;
        return `<img${attributes.replace(/style="[^"]*?"/, `style="${newStyle}"`)}>`;
      } else {
        // style 속성이 없으면 새로 추가
        return `<img style="max-width: 100%; height: auto;"${attributes}>`;
      }
    });
    
    return result;
  }, [contents]);

  // 🔽 본문 내 <img> src 리스트 추출 (편집 화면의 attachedFiles 로직 이식, 클릭/선택 제거)
  const attachedImages = useMemo(() => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(contents || '', 'text/html');
      return Array.from(doc.querySelectorAll('img'))
        .map((img) => img.getAttribute('src') || '')
        .filter((src) => !!src);
    } catch {
      return [];
    }
  }, [contents]);

  const forceDownload = async (url: string, filename: string) => {
    try {
      const response = await fetch(url, { mode: 'cors' });
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('다운로드 실패:', error);
      window.open(url, '_blank');
    }
  };

  return (
    <div style={{ width: '100%' }}>
      {/* 제목 */}
      <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginTop: '8px', fontSize: '14px', color: '#6C757D', width: '100%' }}>
        <div style={{ fontWeight: '700', minWidth: '48px', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
          제목
        </div>
        <div style={{ flex: 1 }}>
          <BTInputBox value={title} disabled />
        </div>
      </div>

      {/* 본문 */}
      <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', marginTop: '8px', fontSize: '14px', color: '#6C757D', width: '100%' }}>
        <div style={{ fontWeight: '700', minWidth: '48px', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', lineHeight: '28px' }}>
          본문
        </div>
        <div style={{ flex: 1 }}>
          <BTMultilineHTMLReader html={processedHtml} borderColor="#adb5bd" minHeight="250px" width="100%" />
        </div>
      </div>

      <BTBox height="16px" />
      {/* 🔽 첨부 이미지 (읽기 전용 썸네일 리스트) */}
      {attachedImages.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
          <div style={{ minWidth: '64px', fontSize: '14px', color: '#6C757D', lineHeight: '28px' }}>
            <b>첨부 이미지</b>
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {attachedImages.map((src, idx) => (
              <div
                key={`${src}-${idx}`}
                style={{
                  width: '100px',
                  height: '100px',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#fafafa',
                }}
              >
                <img
                  src={src}
                  alt={`첨부이미지${idx + 1}`}
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <BTBox height="16px" />

      {/* 첨부 파일 */}
      {attachedFiles.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
          <div style={{ minWidth: '64px', fontSize: '14px', color: '#6C757D', lineHeight: '28px' }}>
            <b>첨부파일</b>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
            {attachedFiles.map((file, index) => {
              const splitUrl = file.split('?')[0];
              const cleanUrl = decodeURIComponent(splitUrl.substring(splitUrl.lastIndexOf('/') + 1));
              const filename = cleanUrl.substring(cleanUrl.lastIndexOf('/') + 1);

              return (
                <span
                  key={index}
                  onClick={() => forceDownload(file, filename)}
                  title={filename}
                  style={{
                    fontSize: '14px',
                    color: '#6C757D',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    whiteSpace: 'normal',
                    overflow: 'visible',
                    textOverflow: 'unset',
                    wordBreak: 'break-all',
                    lineHeight: '28px',
                    display: 'block',
                    maxWidth: '100%',
                  }}
                >
                  {filename}
                </span>
              );
            })}
          </div>
        </div>
      )}

      <BTBox height="36px" />

      <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {onEdit && <BTButton color="#FC5A5A" textColor="white" onClick={onDelete}>삭제</BTButton>}
        <div style={{ display: 'flex', gap: '24px' }}>
          {onEdit && <BTButton color={theme.colors.bbTech} textColor="white" onClick={onEdit}>수정</BTButton>}
          {onBack && <BTButton onClick={onBack}>목록</BTButton>}
        </div>
      </div>
    </div>
  );
};

export default BTReader;